import { tr } from "./tr.js";
import { en } from "./en.js";

/**
 * i18n — Rapor 14 §8, F1-38.
 *
 * ### ⚠️ METİN BİRLEŞTİRME YOK
 * `t("joined") + " " + name` **yasaktır**. Türkçe ekleri, İngilizce sözcük
 * sırası ve çoğul kuralları birleştirmeyle doğru çıkmaz. Her mesaj **tam bir
 * cümledir** ve değişkenleri `{ad}` yer tutucularıyla alır.
 *
 * ### ⚠️ EK KÜTÜPHANE YOK
 * Çoğul ve biçimlendirme `Intl` ile yapılır (`PluralRules`, `DateTimeFormat`,
 * `RelativeTimeFormat`). Bir i18n kütüphanesi kritik yol bütçesine
 * (≤110 KB) 10–40 KB ekler ve bu ürün için karşılığı yoktur.
 *
 * ### Çoğul
 * ICU'nun tam sözdizimi yerine `Intl.PluralRules`'un döndürdüğü kategori
 * (`one`/`other`…) anahtar soneki olarak kullanılır: `participants_one`,
 * `participants_other`. Türkçede tek kategori (`other`) vardır; İngilizcede iki.
 */

export const LOCALES = ["tr", "en"] as const;
export type Locale = (typeof LOCALES)[number];

export type MessageKey = keyof typeof tr;
export type Messages = Record<MessageKey, string> & Record<string, string>;

const CATALOGS: Record<Locale, Messages> = { tr, en };

/**
 * Tarayıcı dilini seçer. ⚠️ `navigator.language` `tr-TR` gibi bölgeli gelir;
 * yalnız birincil alt etiket kullanılır.
 */
export function detectLocale(): Locale {
  for (const lang of navigator.languages ?? [navigator.language]) {
    const primary = lang.toLowerCase().split("-")[0];
    if (primary && (LOCALES as readonly string[]).includes(primary)) return primary as Locale;
  }
  return "tr";
}

export interface Translator {
  locale: Locale;
  /** Tam cümle döndürür. Eksik anahtar **anahtarın kendisini** döndürür — sessiz boş metin değil. */
  t(key: MessageKey, vars?: Record<string, string | number>): string;
  /** Çoğul kategorisine göre `key_one` / `key_other` seçer. */
  plural(key: string, count: number, vars?: Record<string, string | number>): string;
  formatTime(ms: number): string;
  formatDate(ms: number): string;
  formatRelative(ms: number): string;
  formatDuration(seconds: number): string;
}

export function createTranslator(locale: Locale): Translator {
  const catalog = CATALOGS[locale];
  const pluralRules = new Intl.PluralRules(locale);
  const timeFmt = new Intl.DateTimeFormat(locale, { hour: "2-digit", minute: "2-digit" });
  const dateFmt = new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
  const relFmt = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });

  const interpolate = (template: string, vars?: Record<string, string | number>): string =>
    vars === undefined
      ? template
      : template.replace(/\{(\w+)\}/g, (whole, name: string) =>
          Object.hasOwn(vars, name) ? String(vars[name]) : whole,
        );

  return {
    locale,

    t(key, vars) {
      const template = catalog[key];
      // ⚠️ Eksik anahtar SESSİZCE boş metne dönmez: geliştirme sırasında
      //    anahtarın kendisi görünür ve hata hemen fark edilir.
      if (typeof template !== "string") return String(key);
      return interpolate(template, vars);
    },

    plural(key, count, vars) {
      const category = pluralRules.select(count);
      const catalogRecord = catalog as unknown as Record<string, string | undefined>;
      const template = catalogRecord[`${key}_${category}`] ?? catalogRecord[`${key}_other`];
      if (typeof template !== "string") return `${key}_${category}`;
      return interpolate(template, { ...vars, count });
    },

    formatTime: (ms) => timeFmt.format(ms),
    formatDate: (ms) => dateFmt.format(ms),

    formatRelative(ms) {
      const deltaSec = Math.round((ms - Date.now()) / 1000);
      const abs = Math.abs(deltaSec);
      if (abs < 60) return relFmt.format(deltaSec, "second");
      if (abs < 3600) return relFmt.format(Math.round(deltaSec / 60), "minute");
      if (abs < 86400) return relFmt.format(Math.round(deltaSec / 3600), "hour");
      return relFmt.format(Math.round(deltaSec / 86400), "day");
    },

    formatDuration(seconds) {
      const h = Math.floor(seconds / 3600);
      const m = Math.floor((seconds % 3600) / 60);
      const s = Math.floor(seconds % 60);
      const pad = (n: number): string => String(n).padStart(2, "0");
      return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
    },
  };
}

/**
 * ⚠️ Katalogların **aynı anahtarlara** sahip olduğu derleme zamanında zorlanır:
 *    `en` sözlüğü `Messages` tipini uygular. Eksik çeviri kod derlenmez —
 *    "İngilizce'de o metin yoktu" sınıfı hata imkânsız hâle gelir.
 */
export type { Messages as CentvoxMessages };
